/**
 * Cart drawer - AJAX remove, quantity, subscription, and gift offer handlers.
 * Integrates with Shopify Cart API and theme cart:change / cart:refresh events.
 *
 * Gift flows (subscription gift, FEBOFFER, FREEMAG): client-side only. All add/remove
 * and discount apply use Storefront Cart API (cart/add.js, cart/change.js, cart/update.js).
 * No server-side gift logic, no recurring server load. Liquid only outputs variant IDs
 * into data attributes once per section render; JS drives all cart updates and recalls
 * are minimised via debounce (cart:refresh 50ms), in-progress flags, and single-section
 * section requests.
 */
(function() {
  'use strict';
  var DEBUG = false; /* set to true for on-screen cart/gift debug panel */

  var debugEl = null;
  function cartDebugLog(label, data) {
    if (!DEBUG) return;
    var now = new Date();
    var ts = now.getHours() + ':' + now.getMinutes() + ':' + now.getSeconds() + '.' + (now.getMilliseconds() + '').padStart(3, '0');
    if (!debugEl) {
      debugEl = document.createElement('div');
      debugEl.id = 'cart-debug-panel';
      debugEl.setAttribute('style', 'position:fixed;bottom:0;left:0;right:0;max-height:280px;overflow:auto;background:rgba(0,0,0,0.92);color:#0f0;font:11px/1.4 monospace;padding:8px;z-index:999999;border-top:2px solid #0f0;pointer-events:auto;');
      var clearBtn = document.createElement('button');
      clearBtn.textContent = 'Clear';
      clearBtn.setAttribute('style', 'position:sticky;left:0;margin-bottom:4px;padding:4px 8px;cursor:pointer;');
      clearBtn.onclick = function() { debugEl.innerHTML = ''; debugEl.appendChild(clearBtn); };
      debugEl.appendChild(clearBtn);
      document.body.appendChild(debugEl);
    }
    var line = document.createElement('div');
    line.setAttribute('style', 'margin:2px 0;word-break:break-all;');
    var dataStr = data !== undefined ? (typeof data === 'object' ? JSON.stringify(data) : String(data)) : '';
    line.textContent = '[' + ts + '] ' + label + (dataStr ? ' ' + dataStr : '');
    var clearBtn = debugEl.querySelector('button');
    if (clearBtn && clearBtn.nextSibling) debugEl.insertBefore(line, clearBtn.nextSibling);
    else debugEl.appendChild(line);
  }

  if (DEBUG) {
    var _fetch = window.fetch;
    window.fetch = function(url, opts) {
      var u = (typeof url === 'string' ? url : (url && url.url) || '');
      if (u.indexOf('/cart') !== -1) {
        var method = (opts && opts.method) || 'GET';
        var body = (opts && opts.body) || '';
        if (typeof body !== 'string') body = JSON.stringify(body);
        cartDebugLog('CART REQ', { method: method, path: u.split('?')[0].replace(/.*\/cart/, '/cart'), body: body ? body.substring(0, 220) : '' });
        return _fetch.apply(this, arguments).then(function(r) {
          return r.clone().text().then(function(t) {
            var o = {};
            try {
              var j = JSON.parse(t);
              o = {
                ok: r.ok,
                status: r.status,
                item_count: j.item_count,
                discount_count: (j.cart_level_discount_applications || j.discount_codes || []).length,
                discount_titles: (j.cart_level_discount_applications || []).map(function(d) { return d.title; }),
                has_sections: !!(j.sections && Object.keys(j.sections).length),
                err: j.message || j.description || null
              };
            } catch (_) {
              o = { ok: r.ok, status: r.status, textLen: t.length };
            }
            cartDebugLog('CART RES', o);
            return r;
          });
        });
      }
      return _fetch.apply(this, arguments);
    };
  }

  var quantityChangeTimeout = {};
  var QUANTITY_DEBOUNCE_MS = 300;

  function getShopifyRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/');
  }

  function findInComposedPath(e, selector) {
    var path = e.composedPath ? e.composedPath() : (function() {
      var p = []; var t = e.target; while (t) { p.push(t); t = t.parentNode; } return p;
    })();
    for (var i = 0; i < path.length; i++) {
      if (path[i].nodeType === 1 && path[i].matches && path[i].matches(selector)) return path[i];
    }
    return null;
  }

  function getCartDrawerSectionId() {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return null;
    var sectionId = cartDrawer.getAttribute('data-section-id');
    if (sectionId) return sectionId;
    var section = cartDrawer.closest('.shopify-section');
    if (!section || !section.id) return null;
    return section.id.replace('shopify-section-', '');
  }

  function getThemeSectionId() {
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return 'no-cart-drawer';
    var section = cartDrawer.closest('.shopify-section');
    if (!section || !section.id) return 'no-section';
    return section.id.replace('shopify-section-', '');
  }

  /** Section ID(s) to request in cart API - one section is enough for drawer HTML, keeps responses fast */
  function getSectionsArray() {
    var sectionId = getCartDrawerSectionId();
    var themeSectionId = getThemeSectionId();
    if (sectionId) return [sectionId];
    if (themeSectionId) return [themeSectionId];
    return ['cart-drawer'];
  }

  /** Fetch with single retry on 429 (rate limit) after 2s - reduces failed requests under high traffic */
  function fetchWithRetry(url, options, retriesLeft) {
    if (retriesLeft == null) retriesLeft = 1;
    return fetch(url, options).then(function(r) {
      if (r.status === 429 && retriesLeft > 0) {
        return new Promise(function(resolve) { setTimeout(resolve, 2000); }).then(function() {
          return fetchWithRetry(url, options, retriesLeft - 1);
        });
      }
      return r;
    });
  }

  function dispatchCartChange(cart, themeSectionId, sectionId, sectionKeys) {
    if (cart.sections[themeSectionId]) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { cart: cart, baseEvent: 'line-item:change' }
      }));
      return true;
    }
    if (sectionId && cart.sections[sectionId]) {
      var patchedCart = Object.assign({}, cart);
      patchedCart.sections = Object.assign({}, patchedCart.sections);
      patchedCart.sections[themeSectionId] = patchedCart.sections[sectionId];
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { cart: patchedCart, baseEvent: 'line-item:change' }
      }));
      return true;
    }
    if (sectionKeys.length > 0) {
      var firstKey = sectionKeys[0];
      var patchedCart = Object.assign({}, cart);
      patchedCart.sections = Object.assign({}, patchedCart.sections);
      patchedCart.sections[themeSectionId] = cart.sections[firstKey];
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { cart: patchedCart, baseEvent: 'line-item:change' }
      }));
      return true;
    }
    return false;
  }

  var checkoutLockCount = 0;
  function setCheckoutUpdating(updating) {
    var btn = document.querySelector('#cart-drawer .cart-drawer__checkout-button');
    var form = document.getElementById('cart-drawer-form');
    if (!btn && !form) return;
    if (updating) {
      checkoutLockCount++;
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('cart-drawer__checkout-button--updating');
      }
      if (form) form.style.pointerEvents = 'none';
    } else {
      checkoutLockCount = Math.max(0, checkoutLockCount - 1);
      if (checkoutLockCount === 0) {
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          btn.classList.remove('cart-drawer__checkout-button--updating');
        }
        if (form) form.style.pointerEvents = '';
      }
    }
  }

  function showGiftLoader(message) {
    setCheckoutUpdating(true);
    var container = document.querySelector('#cart-drawer .cart-drawer__inner');
    if (!container) return;
    var overlay = document.getElementById('cart-drawer-gift-loader');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'cart-drawer-gift-loader';
      overlay.className = 'cart-drawer__gift-loader';
      overlay.setAttribute('aria-live', 'polite');
      overlay.setAttribute('aria-busy', 'true');
      container.appendChild(overlay);
    }
    overlay.innerHTML = '<span class="cart-drawer__gift-loader-spinner"></span><span class="cart-drawer__gift-loader-text">' + (message || 'Updating...') + '</span>';
    overlay.style.display = 'flex';
  }
  function hideGiftLoader() {
    setCheckoutUpdating(false);
    var overlay = document.getElementById('cart-drawer-gift-loader');
    if (overlay) overlay.style.display = 'none';
  }

  function setupCartDrawerListeners() {
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return false;
    if (cartDrawer._cartListenersAttached) return true;
    cartDrawer._cartListenersAttached = true;

    cartDrawer.addEventListener('click', function(e) {
      var sub = findInComposedPath(e, '.cart-drawer__subscribe-button');
      var freqSelect = findInComposedPath(e, '.cart-drawer__frequency-select');
      var subArea = findInComposedPath(e, '.cart-drawer__item-subscription');
      if (sub) {
        e.preventDefault();
        e.stopPropagation();
        handleSubscribeClick(sub);
      } else if (freqSelect) {
        return;
      } else if (subArea) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    return true;
  }

  async function handleSubscribeClick(subscribeBtn) {
    if (subscribeBtn.getAttribute('aria-busy') === 'true') return;
    var sellingPlanId = subscribeBtn.getAttribute('data-selling-plan-id');
    if (!sellingPlanId) return;
    var lineKey = subscribeBtn.getAttribute('data-line-key');
    var quantity = parseInt(subscribeBtn.getAttribute('data-quantity'), 10) || 1;
    if (!lineKey) return;
    var sections = getSectionsArray();
    var root = getShopifyRoot();
    var themeSectionId = getThemeSectionId();
    var sectionId = getCartDrawerSectionId();
    subscribeBtn.disabled = true;
    subscribeBtn.setAttribute('aria-busy', 'true');
    subscribeBtn.textContent = 'Updating...';
    setCheckoutUpdating(true);
    try {
      var res = await fetchWithRetry(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity, selling_plan: parseInt(sellingPlanId, 10), sections: sections })
      });
      var text = await res.text();
      var cart;
      try { cart = JSON.parse(text); } catch (err) {
        setCheckoutUpdating(false);
        subscribeBtn.disabled = false;
        subscribeBtn.removeAttribute('aria-busy');
        subscribeBtn.textContent = 'Subscribe Risk-Free - 15% Off + Free Gifts';
        return;
      }
      var sectionKeys = cart && cart.sections ? Object.keys(cart.sections) : [];
      if (res.ok && cart && cart.sections) {
        cartDebugLog('SUBSCRIBE', { afterChange: 'cart has subscription', item_count: cart.item_count, running: 'runFebOfferCheck then runSubscriptionGiftCheck then runFreemagCheck' });
        runFebOfferCheck(cart, 'subscribe');
        runSubscriptionGiftCheck(cart, 'subscribe');
        runFreemagCheck(cart, 'subscribe');
        var giftWillChangeSub = febofferInProgress || subscriptionGiftInProgress || febofferPending || freemagInProgress;
        if (!giftWillChangeSub) {
          dispatchCartChange(cart, themeSectionId, sectionId, sectionKeys);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(function() { setCheckoutUpdating(false); }, 2000);
        } else {
          setTimeout(function() { setCheckoutUpdating(false); }, 10000);
        }
      } else {
        setCheckoutUpdating(false);
      }
      subscribeBtn.disabled = false;
      subscribeBtn.removeAttribute('aria-busy');
      subscribeBtn.textContent = 'Subscribe Risk-Free - 15% Off + Free Gifts';
    } catch (err) {
      setCheckoutUpdating(false);
      subscribeBtn.disabled = false;
      subscribeBtn.removeAttribute('aria-busy');
      subscribeBtn.textContent = 'Subscribe Risk-Free - 15% Off + Free Gifts';
    }
  }

  function initWhenReady() {
    if (setupCartDrawerListeners()) return;
    var attempts = 0;
    var id = setInterval(function() {
      attempts++;
      if (setupCartDrawerListeners() || attempts > 100) clearInterval(id);
    }, 100);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady);
  } else {
    initWhenReady();
  }

  document.addEventListener('click', function(e) {
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.show !== 'function') return;
    var cartType = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType) || '';
    if (cartType === 'page') return;
    var path = window.location.pathname || '';
    if (path.replace(/\/$/, '').endsWith('/cart')) return;
    var el = e.target.closest ? e.target.closest('a[href*="/cart"], a[href*="cart"]') : null;
    if (!el || el.getAttribute('aria-controls') === 'cart-drawer') return;
    var href = (el.getAttribute('href') || '').toLowerCase();
    if (href.indexOf('checkout') >= 0) return;
    if (href.indexOf('/cart') >= 0 || href === '/cart' || href.endsWith('/cart')) {
      e.preventDefault();
      e.stopPropagation();
      cartDrawer.show();
    }
  }, true);

  document.addEventListener('click', function(e) {
    var sub = findInComposedPath(e, '.cart-drawer__subscribe-button');
    if (sub) {
      e.preventDefault();
      e.stopPropagation();
      handleSubscribeClick(sub);
    }
  }, true);

  document.addEventListener('change', function(e) {
    var target = e.target;
    var freqSelect = target.matches && target.matches('.cart-drawer__frequency-select') ? target : null;
    if (freqSelect) {
      e.stopPropagation();
      e.stopImmediatePropagation();
      var lineKey = freqSelect.getAttribute('data-line-key');
      var quantity = parseInt(freqSelect.getAttribute('data-quantity'), 10) || 1;
      var val = freqSelect.value;
      if (!lineKey) return;
      var sellingPlan = val === 'onetime' ? null : parseInt(val, 10);
      if (val !== 'onetime' && isNaN(sellingPlan)) return;
      freqSelect.disabled = true;
      setCheckoutUpdating(true);
      var lineItem = freqSelect.closest('.cart-drawer__item');
      if (lineItem) { lineItem.style.opacity = '0.6'; lineItem.style.pointerEvents = 'none'; }
      var sections = getSectionsArray();
      var root = getShopifyRoot();
      fetchWithRetry(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity, selling_plan: sellingPlan, sections: sections })
      }).then(function(r) { return r.text().then(function(t) { return { ok: r.ok, text: t, status: r.status }; }); }).then(function(res) {
        var cart;
        try { cart = JSON.parse(res.text); } catch (err) {
          setCheckoutUpdating(false);
          freqSelect.disabled = false;
          if (lineItem) { lineItem.style.opacity = ''; lineItem.style.pointerEvents = ''; }
          return;
        }
        if (res.ok && cart && cart.sections) {
          var themeSectionId = getThemeSectionId();
          var sectionId = getCartDrawerSectionId();
          var sectionKeys = Object.keys(cart.sections);
          runFebOfferCheck(cart, 'frequency-change');
          runSubscriptionGiftCheck(cart, 'frequency-change');
          runFreemagCheck(cart, 'frequency-change');
          var giftWillChangeFreq = febofferInProgress || subscriptionGiftInProgress || febofferPending || freemagInProgress;
          if (!giftWillChangeFreq) {
            dispatchCartChange(cart, themeSectionId, sectionId, sectionKeys);
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            setCheckoutUpdating(false);
            freqSelect.disabled = false;
            if (lineItem) { lineItem.style.opacity = ''; lineItem.style.pointerEvents = ''; }
          } else {
            freqSelect.disabled = false;
            if (lineItem) { lineItem.style.opacity = ''; lineItem.style.pointerEvents = ''; }
            setTimeout(function() {
              setCheckoutUpdating(false);
            }, 10000);
          }
        } else {
          setCheckoutUpdating(false);
          freqSelect.disabled = false;
          if (lineItem) { lineItem.style.opacity = ''; lineItem.style.pointerEvents = ''; }
        }
      }).catch(function() {
        setCheckoutUpdating(false);
        freqSelect.disabled = false;
        if (lineItem) { lineItem.style.opacity = ''; lineItem.style.pointerEvents = ''; }
      });
      return;
    }
    var input = target;
    if (!input.matches || !input.matches('.cart-drawer__quantity-input, .quantity-input')) return;
    var lineKey = input.getAttribute('data-line-key');
    if (!lineKey) return;
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer || !cartDrawer.contains(input)) return;

    var targetQty = parseInt(input.value, 10);
    if (isNaN(targetQty) || targetQty < 1) {
      input.value = input.getAttribute('min') || 1;
      return;
    }
    var max = input.getAttribute('max');
    if (max && targetQty > parseInt(max, 10)) {
      input.value = max;
      targetQty = parseInt(max, 10);
    }

    var lineItem = input.closest('.cart-drawer__item');
    if (lineItem) {
      lineItem.style.opacity = '0.6';
      lineItem.style.pointerEvents = 'none';
    }
    setCheckoutUpdating(true);
    clearTimeout(quantityChangeTimeout[lineKey]);
    quantityChangeTimeout[lineKey] = setTimeout(function() {
      delete quantityChangeTimeout[lineKey];
      setTimeout(function() {
        if (!febofferInProgress && !subscriptionGiftInProgress && !febofferPending && !freemagInProgress) {
          setCheckoutUpdating(false);
        }
      }, 2000);
    }, QUANTITY_DEBOUNCE_MS);
  }, true);

  document.addEventListener('line-item:error', function(e) {
    var lineItem = e.target && e.target.closest ? e.target.closest('.cart-drawer__item') : null;
    if (lineItem) {
      lineItem.style.opacity = '';
      lineItem.style.pointerEvents = '';
    }
    setCheckoutUpdating(false);
  });

  document.addEventListener('click', async function(e) {
    const btn = findInComposedPath(e, '.cart-drawer__item-remove[data-line-key]') || e.target.closest('.cart-drawer__item-remove[data-line-key]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const lineKey = btn.getAttribute('data-line-key');
    if (!lineKey) return;

    const sectionId = getCartDrawerSectionId();
    const themeSectionId = getThemeSectionId();
    var sections = getSectionsArray();
    var root = getShopifyRoot();
    const url = root + 'cart/change.js';

    var lineItem = btn.closest('.cart-drawer__item');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    setCheckoutUpdating(true);

    var loader = document.createElement('div');
    loader.className = 'cart-drawer__remove-loader';
    loader.innerHTML = '<span class="cart-drawer__remove-loader-spinner"></span>';
    if (lineItem) {
      lineItem.style.position = 'relative';
      lineItem.style.opacity = '0.6';
      lineItem.style.pointerEvents = 'none';
      lineItem.appendChild(loader);
    }

    try {
      const requestBody = { id: lineKey, quantity: 0, sections: sections };
      const response = await fetchWithRetry(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      var text = await response.text();
      var cart;
      try {
        cart = JSON.parse(text);
      } catch (parseErr) {
        setCheckoutUpdating(false);
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        if (lineItem) {
          lineItem.style.opacity = '';
          lineItem.style.pointerEvents = '';
          var l = lineItem.querySelector('.cart-drawer__remove-loader');
          if (l) l.remove();
        }
        return;
      }
      var sectionKeys = cart && cart.sections ? Object.keys(cart.sections) : [];

      if (response.ok && cart) {
        runFebOfferCheck(cart, 'remove');
        runSubscriptionGiftCheck(cart, 'remove');
        runFreemagCheck(cart, 'remove');
        var giftWillChange = febofferInProgress || subscriptionGiftInProgress || febofferPending || freemagInProgress;
        if (!giftWillChange) {
          cart.sections && dispatchCartChange(cart, themeSectionId, sectionId, sectionKeys);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        if (giftWillChange) {
          setTimeout(function() {
            setCheckoutUpdating(false);
            if (btn.parentNode) {
              btn.removeAttribute('aria-busy');
              btn.disabled = false;
            }
            if (lineItem && lineItem.parentNode) {
              lineItem.style.opacity = '';
              lineItem.style.pointerEvents = '';
              var l = lineItem.querySelector('.cart-drawer__remove-loader');
              if (l) l.remove();
            }
          }, 10000);
        } else {
          setTimeout(function() {
            if (!febofferInProgress && !subscriptionGiftInProgress && !febofferPending && !freemagInProgress) {
              setCheckoutUpdating(false);
              btn.removeAttribute('aria-busy');
              btn.disabled = false;
              if (lineItem && lineItem.parentNode) {
                lineItem.style.opacity = '';
                lineItem.style.pointerEvents = '';
                var l = lineItem.querySelector('.cart-drawer__remove-loader');
                if (l) l.remove();
              }
            }
          }, 2000);
        }
      } else {
        if (response.ok && cart && cart.items) {
          runFebOfferCheck(cart, 'remove-fallback');
          runSubscriptionGiftCheck(cart, 'remove-fallback');
          runFreemagCheck(cart, 'remove-fallback');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          var giftWillChangeFallback = febofferInProgress || subscriptionGiftInProgress || febofferPending || freemagInProgress;
          if (!giftWillChangeFallback) {
            setTimeout(function() {
              if (!febofferInProgress && !subscriptionGiftInProgress && !febofferPending && !freemagInProgress) {
                setCheckoutUpdating(false);
                btn.removeAttribute('aria-busy');
                btn.disabled = false;
                if (lineItem && lineItem.parentNode) {
                  lineItem.style.opacity = '';
                  lineItem.style.pointerEvents = '';
                  var l = lineItem.querySelector('.cart-drawer__remove-loader');
                  if (l) l.remove();
                }
              }
            }, 2000);
          } else {
            setTimeout(function() {
              setCheckoutUpdating(false);
              if (btn.parentNode) {
                btn.removeAttribute('aria-busy');
                btn.disabled = false;
              }
              if (lineItem && lineItem.parentNode) {
                lineItem.style.opacity = '';
                lineItem.style.pointerEvents = '';
                var l = lineItem.querySelector('.cart-drawer__remove-loader');
                if (l) l.remove();
              }
            }, 10000);
          }
        } else {
          setCheckoutUpdating(false);
          btn.removeAttribute('aria-busy');
          btn.disabled = false;
          if (lineItem) {
            lineItem.style.opacity = '';
            lineItem.style.pointerEvents = '';
            if (loader.parentNode) loader.remove();
          }
        }
      }
    } catch (err) {
      setCheckoutUpdating(false);
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      if (lineItem) {
        lineItem.style.opacity = '';
        lineItem.style.pointerEvents = '';
        loader.remove();
      }
    }
  });

  /* Subscription gift: auto-add when cart has subscription, auto-remove when no subscription */
  var subscriptionGiftInProgress = false;
  function runSubscriptionGiftCheck(cart, trigger) {
    if (!cart || !cart.items) {
      cartDebugLog('SUB_GIFT', { trigger: trigger, skip: 'no cart/items' });
      return;
    }
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    if (cartDrawer.getAttribute('data-wholesale') === 'true') return;
    var giftVariantIdRaw = cartDrawer.getAttribute('data-subscription-gift-variant-id');
    if (!giftVariantIdRaw || giftVariantIdRaw === '' || giftVariantIdRaw === 'nil') return;
    var giftVariantId = parseInt(giftVariantIdRaw, 10);
    if (isNaN(giftVariantId)) return;
    var blocked = subscriptionGiftInProgress || febofferInProgress || febofferPending || freemagInProgress;
    if (blocked) {
      cartDebugLog('SUB_GIFT', { trigger: trigger, skip: 'blocked', sub: subscriptionGiftInProgress, feb: febofferInProgress, febP: febofferPending, mag: freemagInProgress });
      return;
    }

    var hasSubscription = cart.items.some(function(item) {
      return item.selling_plan_allocation != null;
    });
    var giftLine = cart.items.find(function(item) {
      return item.variant_id === giftVariantId && item.properties && item.properties._subscription_gift === '1';
    });
    var giftAlreadyInCart = cart.items.some(function(item) { return item.variant_id === giftVariantId; });

    cartDebugLog('SUB_GIFT', { trigger: trigger, hasSubscription: hasSubscription, giftAlreadyInCart: giftAlreadyInCart, giftLine: !!giftLine, item_count: cart.items.length });

    var root = getShopifyRoot();

    if (hasSubscription && giftAlreadyInCart) {
      var onlyApplyAfterOfferRemoved = (trigger === 'after-freemag-removed' || trigger === 'after-feboffer-removed');
      if (!onlyApplyAfterOfferRemoved) {
        cartDebugLog('SUB_GIFT', { skip: 'gift in cart, no apply (only apply after offer removed)' });
        return;
      }
      var applications = cart.cart_level_discount_applications || cart.discount_applications || [];
      var hasSubDiscount = applications.length && applications.some(function(a) {
        var t = (a.title || a.code || '').toUpperCase();
        return t === 'VTTKQNMCWZJQ';
      });
      if (!hasSubDiscount) {
        cartDebugLog('SUB_GIFT', { action: 'APPLY discount only (gift already in cart)' });
        subscriptionGiftInProgress = true;
        showGiftLoader('Applying discount...');
        var sectionsApply = getSectionsArray();
        var themeSectionIdApply = getThemeSectionId();
        var sectionIdApply = getCartDrawerSectionId();
        fetchWithRetry(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: 'VTTKQNMCWZJQ', sections: sectionsApply.join(','), sections_url: '/cart' })
        }).then(function(r) { return r.text().then(function(t) { return { text: t, ok: r.ok }; }); }).then(function(updateRes) {
          var updateData;
          try { updateData = JSON.parse(updateRes.text); } catch (_) {}
          if (updateRes.ok && updateData && updateData.sections && Object.keys(updateData.sections).length > 0) {
            dispatchCartChange(updateData, themeSectionIdApply, sectionIdApply, Object.keys(updateData.sections));
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          subscriptionGiftInProgress = false;
          hideGiftLoader();
          cartDebugLog('SUB_GIFT', { done: 'apply discount only' });
        }).catch(function() {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          subscriptionGiftInProgress = false;
          hideGiftLoader();
        });
      } else {
        cartDebugLog('SUB_GIFT', { skip: 'gift in cart, discount already applied' });
      }
      return;
    }

    if (hasSubscription && !giftAlreadyInCart) {
      cartDebugLog('SUB_GIFT', { action: 'ADD gift + apply VTTKQNMCWZJQ' });
      subscriptionGiftInProgress = true;
      showGiftLoader('Adding your gift...');
      var sections = getSectionsArray();
      var themeSectionId = getThemeSectionId();
      var sectionId = getCartDrawerSectionId();
      /* add.js without sections = faster; section HTML comes from update.js below */
      fetchWithRetry(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: giftVariantId, quantity: 1, properties: { '_subscription_gift': '1' } }]
        })
      }).then(function(r) { return r.text().then(function(t) { return { text: t, ok: r.ok, status: r.status }; }); }).then(function(res) {
        var addData;
        try { addData = JSON.parse(res.text); } catch (_) {}
        if (addData && (addData.id || addData.items)) {
          fetchWithRetry(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: 'VTTKQNMCWZJQ', sections: sections.join(','), sections_url: '/cart' })
          }).then(function(r2) { return r2.text().then(function(t2) { return { text: t2, ok: r2.ok }; }); }).then(function(updateRes) {
            var updateData;
            try { updateData = JSON.parse(updateRes.text); } catch (_) {}
            if (updateRes.ok && updateData && updateData.sections && Object.keys(updateData.sections).length > 0) {
              var sectionKeys = Object.keys(updateData.sections);
              var cartForDispatch = { sections: updateData.sections, item_count: updateData.item_count || cart.item_count + 1 };
              dispatchCartChange(cartForDispatch, themeSectionId, sectionId, sectionKeys);
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            } else if (addData.sections && Object.keys(addData.sections).length > 0) {
              var sectionKeys = Object.keys(addData.sections);
              var cartForDispatch = { sections: addData.sections, item_count: cart.item_count + 1 };
              dispatchCartChange(cartForDispatch, themeSectionId, sectionId, sectionKeys);
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            } else {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }
            hideGiftLoader();
            subscriptionGiftInProgress = false;
            cartDebugLog('SUB_GIFT', { done: 'add+discount', subscriptionGiftInProgress: false });
          }).catch(function() {
            if (addData.sections && Object.keys(addData.sections).length > 0) {
              var sectionKeys = Object.keys(addData.sections);
              var cartForDispatch = { sections: addData.sections, item_count: cart.item_count + 1 };
              dispatchCartChange(cartForDispatch, themeSectionId, sectionId, sectionKeys);
            }
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            hideGiftLoader();
            subscriptionGiftInProgress = false;
            cartDebugLog('SUB_GIFT', { done: 'add+discount (catch)', subscriptionGiftInProgress: false });
          });
        } else {
          cartDebugLog('SUB_GIFT', { done: 'add failed (no id/items)', addData: addData ? Object.keys(addData) : null });
          hideGiftLoader();
          subscriptionGiftInProgress = false;
        }
      }).catch(function() {
        cartDebugLog('SUB_GIFT', { done: 'add catch', subscriptionGiftInProgress: false });
        hideGiftLoader();
        subscriptionGiftInProgress = false;
      });
    } else if (!hasSubscription && giftLine) {
      cartDebugLog('SUB_GIFT', { action: 'REMOVE gift + clear discount' });
      subscriptionGiftInProgress = true;
      showGiftLoader('Removing your gift...');
      var sections = getSectionsArray();
      var themeSectionId = getThemeSectionId();
      var sectionId = getCartDrawerSectionId();
      /* change.js without sections = faster; section HTML comes from update.js below */
      fetchWithRetry(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: giftLine.key, quantity: 0 })
      }).then(function(r) { return r.text().then(function(t) { return { text: t, ok: r.ok }; }); }).then(function(changeRes) {
        return fetchWithRetry(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: '', sections: sections.join(','), sections_url: '/cart' })
        }).then(function(r2) { return r2.text().then(function(t2) { return { text: t2, ok: r2.ok }; }); });
      }).then(function(updateRes) {
        var updateData;
        try { updateData = JSON.parse(updateRes.text); } catch (_) {}
        if (updateRes.ok && updateData && updateData.sections && Object.keys(updateData.sections).length > 0) {
          var sectionKeys = Object.keys(updateData.sections);
          dispatchCartChange(updateData, themeSectionId, sectionId, sectionKeys);
          /* cart:change payload has sections only, no items - dispatch cart:refresh so FEBOFFER can run with full cart */
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        hideGiftLoader();
        subscriptionGiftInProgress = false;
      }).catch(function() {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        hideGiftLoader();
        subscriptionGiftInProgress = false;
      });
    }
  }

  /* FEBOFFER: Private Gift Card offer - variant IDs from cart-drawer data attributes (Theme Editor > Cart drawer > FEBOFFER) */
  var FEBOFFER_TIER1_DISCOUNT = '40XYPS1TFD25';
  var FEBOFFER_TIER2_DISCOUNT = 'VJAQWDW4XJWK';
  var FEBOFFER_MIN_CENTS = 15000;   /* $150 */
  var FEBOFFER_TIER2_CENTS = 50000; /* $500 */
  var febofferInProgress = false;
  var febofferPending = false;
  var freemagInProgress = false;

  var FEBOFFER_PRODUCT_IDS = { tier1: 9016490393819, tier2: 9016490459355 };
  var febofferVariantCache = { tier1: null, tier2: null };

  function getFebOfferVariantIds() {
    var cartDrawer = document.getElementById('cart-drawer');
    var t1 = cartDrawer ? cartDrawer.getAttribute('data-feboffer-tier1-variant-id') : '';
    var t2 = cartDrawer ? cartDrawer.getAttribute('data-feboffer-tier2-variant-id') : '';
    var fromLiquid = {
      tier1: t1 && t1 !== '' ? parseInt(t1, 10) : null,
      tier2: t2 && t2 !== '' ? parseInt(t2, 10) : null
    };
    if (fromLiquid.tier1 || fromLiquid.tier2) return fromLiquid;
    if (febofferVariantCache.tier1 || febofferVariantCache.tier2) return febofferVariantCache;
    return { tier1: null, tier2: null };
  }

  function fetchFebOfferVariantsFromProductIds(cb) {
    if (febofferVariantCache.tier1 && febofferVariantCache.tier2) { if (cb) cb(); return; }
    var root = getShopifyRoot();
    fetch(root + 'products.json?limit=250').then(function(r) { return r.json(); }).then(function(data) {
      var products = data.products || [];
      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        if ((p.id == FEBOFFER_PRODUCT_IDS.tier1 || String(p.id) === '9016490393819') && p.variants && p.variants[0]) {
          febofferVariantCache.tier1 = p.variants[0].id;
        }
        if ((p.id == FEBOFFER_PRODUCT_IDS.tier2 || String(p.id) === '9016490459355') && p.variants && p.variants[0]) {
          febofferVariantCache.tier2 = p.variants[0].id;
        }
      }
      if (cb) cb();
    }).catch(function() { if (cb) cb(); });
  }

  function isFebOfferActive() {
    var EXPIRY_MS = 30 * 60 * 1000;
    var getVal = function(key) {
      var expires = parseInt(sessionStorage.getItem(key + '_expires') || '0', 10);
      if (!expires || Date.now() > expires) return '';
      return sessionStorage.getItem(key) || '';
    };
    return getVal('utm_campaign') === 'FEBOFFER' && getVal('ref') === 'social' && getVal('campaignid') === '3d81bbdaccc3965c9e7be3ea8354ad7fa114ccb5';
  }

  function runFebOfferCheck(cart, trigger) {
    var ids = getFebOfferVariantIds();
    if (!ids.tier1 && !ids.tier2) {
      febofferPending = true;
      fetchFebOfferVariantsFromProductIds(function() {
        ids = getFebOfferVariantIds();
        if (!ids.tier1 && !ids.tier2) {
          febofferPending = false;
          return;
        }
        runFebOfferCheckWithIds(cart, trigger, ids);
        febofferPending = false;
      });
      return;
    }
    runFebOfferCheckWithIds(cart, trigger, ids);
  }

  function runFebOfferCheckWithIds(cart, trigger, ids) {
    var isActive = isFebOfferActive();
    if (!isActive) return;
    if (!cart || !cart.items) return;
    var blocked = subscriptionGiftInProgress || febofferInProgress || febofferPending || freemagInProgress;
    if (blocked) {
      cartDebugLog('FEBOFFER', { trigger: trigger, skip: 'blocked' });
      return;
    }
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer || cartDrawer.getAttribute('data-wholesale') === 'true') return;
    var root = getShopifyRoot();
    var sections = getSectionsArray();
    var themeSectionId = getThemeSectionId();
    var sectionId = getCartDrawerSectionId();
    var hasSubscription = cart.items.some(function(item) { return item.selling_plan_allocation != null; });
    var tier1Line = ids.tier1 ? cart.items.find(function(item) { return item.variant_id === ids.tier1; }) : null;
    var tier2Line = ids.tier2 ? cart.items.find(function(item) { return item.variant_id === ids.tier2; }) : null;

    cartDebugLog('FEBOFFER', { trigger: trigger, hasSubscription: hasSubscription, tier1: !!tier1Line, tier2: !!tier2Line });

    if (hasSubscription) {
      if (tier1Line || tier2Line) {
        var toRemove = (tier2Line || tier1Line).key;
        cartDebugLog('FEBOFFER', { action: 'REMOVE gift (subscription), then runSubscriptionGiftAfter' });
        doUpdate('', null, toRemove, null, true);
      }
      return;
    }

    var eligibleTotal = cart.items.reduce(function(sum, item) {
      if (ids.tier1 && item.variant_id === ids.tier1) return sum;
      if (ids.tier2 && item.variant_id === ids.tier2) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);

    var FEBOFFER_LOADER_MIN_MS = 5000;
    function doUpdate(discount, thenAddVariant, thenRemoveLineKey, cb, runSubscriptionGiftAfter) {
      febofferInProgress = true;
      cartDebugLog('FEBOFFER doUpdate START', { discount: discount || '(clear)', remove: thenRemoveLineKey || null, add: thenAddVariant || null, runSubscriptionGiftAfter: !!runSubscriptionGiftAfter });
      var loaderStart = Date.now();
      showGiftLoader('Updating your offer...');
      var chain = Promise.resolve();
      if (thenRemoveLineKey) {
        chain = chain.then(function() {
          return fetchWithRetry(root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: thenRemoveLineKey, quantity: 0 })
          });
        });
      }
      if (thenAddVariant) {
        chain = chain.then(function() {
          return fetchWithRetry(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: thenAddVariant, quantity: 1, properties: { '_feboffer_gift': '1' } }]
            })
          }).then(function(r) { return r.text(); }).then(function(text) {
            var addData;
            try { addData = JSON.parse(text); } catch (_) {}
            var addOk = addData && (addData.id || (addData.items && addData.items.length));
            var addMsg = addData ? (addData.message || addData.description || (addOk ? 'OK' : JSON.stringify(addData).substring(0, 150))) : text.substring(0, 150);
            if (!addOk && addData) throw new Error(addMsg);
            return text;
          });
        });
      }
      chain.then(function() {
        return fetchWithRetry(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: discount, sections: sections.join(','), sections_url: '/cart' })
        });
      }).then(function(r) { return r.text(); }).then(function(text) {
        var updateData;
        try { updateData = JSON.parse(text); } catch (_) {}
        var updateOk = updateData && updateData.sections && Object.keys(updateData.sections).length > 0;
        var cartForDispatch = updateOk ? { sections: updateData.sections, item_count: updateData.item_count || cart.item_count } : null;
        var hasSections = updateOk && updateData.sections && Object.keys(updateData.sections).length > 0;
        var elapsed = Date.now() - loaderStart;
        var delay = Math.max(0, FEBOFFER_LOADER_MIN_MS - elapsed);
        setTimeout(function() {
          febofferInProgress = false;
          if (hasSections && cartForDispatch) {
            var sectionKeys = Object.keys(cartForDispatch.sections);
            dispatchCartChange(cartForDispatch, themeSectionId, sectionId, sectionKeys);
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
          hideGiftLoader();
          if (cb) cb();
          if (runSubscriptionGiftAfter) {
            cartDebugLog('FEBOFFER', { afterRemove: 'fetching cart then runSubscriptionGiftCheck(after-feboffer-removed)' });
            fetch(root + 'cart.js').then(function(r) { return r.text(); }).then(function(t) {
              var freshCart;
              try { freshCart = JSON.parse(t); } catch (_) {}
              cartDebugLog('FEBOFFER', { runSubscriptionGiftAfter: !!freshCart && !!freshCart.items });
              if (freshCart && freshCart.items) runSubscriptionGiftCheck(freshCart, 'after-feboffer-removed');
            }).catch(function() { cartDebugLog('FEBOFFER', { runSubscriptionGiftAfter: 'fetch failed' }); });
          }
        }, delay);
      }).catch(function() {
        var elapsed = Date.now() - loaderStart;
        var delay = Math.max(0, FEBOFFER_LOADER_MIN_MS - elapsed);
        setTimeout(function() {
          febofferInProgress = false;
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          hideGiftLoader();
          if (cb) cb();
          if (runSubscriptionGiftAfter) {
            cartDebugLog('FEBOFFER', { afterRemoveCatch: 'fetching cart then runSubscriptionGiftCheck' });
            fetch(root + 'cart.js').then(function(r) { return r.text(); }).then(function(t) {
              var freshCart;
              try { freshCart = JSON.parse(t); } catch (_) {}
              if (freshCart && freshCart.items) runSubscriptionGiftCheck(freshCart, 'after-feboffer-removed');
            }).catch(function() {});
          }
        }, delay);
      });
    }

    if (eligibleTotal < FEBOFFER_MIN_CENTS) {
      if (tier1Line || tier2Line) {
        var toRemove = (tier2Line || tier1Line).key;
        doUpdate('', null, toRemove);
      }
      return;
    }

    if (eligibleTotal >= FEBOFFER_TIER2_CENTS) {
      if (tier2Line) return;
      if (ids.tier2) {
        doUpdate(FEBOFFER_TIER2_DISCOUNT, ids.tier2, tier1Line ? tier1Line.key : null);
      }
      return;
    }

    /* $150 - $499.99 */
    if (tier1Line) return;
    if (ids.tier1) {
      doUpdate(FEBOFFER_TIER1_DISCOUNT, ids.tier1, tier2Line ? tier2Line.key : null);
    }
  }

  /* FREEMAG: free Magnesium product when ?discount=FREEMAG and cart >= $120. No subscription. */
  var FREEMAG_DISCOUNT = 'FREEMAG';
  var FREEMAG_MIN_CENTS = 12000; /* $120 */

  function setFreemagFromUrl() {
    if (typeof window === 'undefined' || !window.location || !window.location.search) return;
    if (window.location.search.indexOf('discount=FREEMAG') !== -1) {
      try {
        sessionStorage.removeItem('ref');
        sessionStorage.removeItem('ref_expires');
        sessionStorage.removeItem('utm_campaign');
        sessionStorage.removeItem('utm_campaign_expires');
        sessionStorage.removeItem('campaignid');
        sessionStorage.removeItem('campaignid_expires');
        sessionStorage.setItem('freemag_active', '1');
        sessionStorage.setItem('freemag_active_expires', String(Date.now() + 30 * 60 * 1000));
      } catch (_) {}
    }
  }

  function isFreemagActive() {
    try {
      var expires = parseInt(sessionStorage.getItem('freemag_active_expires') || '0', 10);
      if (!expires || Date.now() > expires) return false;
      return sessionStorage.getItem('freemag_active') === '1';
    } catch (_) { return false; }
  }

  function getFreemagVariantId() {
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return null;
    var raw = cartDrawer.getAttribute('data-freemag-gift-variant-id');
    if (!raw || raw === '' || raw === 'nil') return null;
    var id = parseInt(raw, 10);
    return isNaN(id) ? null : id;
  }

  function runFreemagCheck(cart, trigger) {
    setFreemagFromUrl();
    if (!isFreemagActive()) return;
    if (!cart || !cart.items) return;
    var blocked = subscriptionGiftInProgress || febofferInProgress || febofferPending || freemagInProgress;
    if (blocked) {
      cartDebugLog('FREEMAG', { trigger: trigger, skip: 'blocked' });
      return;
    }
    var cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer || cartDrawer.getAttribute('data-wholesale') === 'true') return;
    var variantId = getFreemagVariantId();
    if (!variantId) return;
    var hasSubscription = cart.items.some(function(item) { return item.selling_plan_allocation != null; });
    if (hasSubscription) {
      var freemagLine = cart.items.find(function(item) {
        return item.variant_id === variantId && item.properties && item.properties._freemag_gift === '1';
      });
      cartDebugLog('FREEMAG', { trigger: trigger, hasSubscription: true, freemagLine: !!freemagLine });
      if (freemagLine) {
        cartDebugLog('FREEMAG', { action: 'REMOVE gift (subscription), then runSubscriptionGiftCheck' });
        freemagInProgress = true;
        showGiftLoader('Updating your offer...');
        var sections = getSectionsArray();
        var themeSectionId = getThemeSectionId();
        var sectionId = getCartDrawerSectionId();
        var root = getShopifyRoot();
        fetchWithRetry(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: freemagLine.key, quantity: 0 })
        }).then(function() {
          return fetchWithRetry(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: '', sections: sections.join(','), sections_url: '/cart' })
          });
        }).then(function(r) { return r.text(); }).then(function(text) {
          var updateData;
          try { updateData = JSON.parse(text); } catch (_) {}
          if (updateData && updateData.sections && Object.keys(updateData.sections).length > 0) {
            dispatchCartChange(updateData, themeSectionId, sectionId, Object.keys(updateData.sections));
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          freemagInProgress = false;
          hideGiftLoader();
          cartDebugLog('FREEMAG', { afterRemove: 'fetching cart then runSubscriptionGiftCheck(after-freemag-removed)' });
          fetch(root + 'cart.js').then(function(r) { return r.text(); }).then(function(t) {
            var freshCart;
            try { freshCart = JSON.parse(t); } catch (_) {}
            if (freshCart && freshCart.items) runSubscriptionGiftCheck(freshCart, 'after-freemag-removed');
          }).catch(function() {});
        }).catch(function() {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          freemagInProgress = false;
          hideGiftLoader();
          fetch(root + 'cart.js').then(function(r) { return r.text(); }).then(function(t) {
            var freshCart;
            try { freshCart = JSON.parse(t); } catch (_) {}
            if (freshCart && freshCart.items) runSubscriptionGiftCheck(freshCart, 'after-freemag-removed');
          }).catch(function() {});
        });
      }
      return;
    }
    var eligibleTotal = cart.items.reduce(function(sum, item) {
      if (item.variant_id === variantId) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
    var freemagLine = cart.items.find(function(item) {
      return item.variant_id === variantId && item.properties && item.properties._freemag_gift === '1';
    });

    if (eligibleTotal < FREEMAG_MIN_CENTS) {
      if (freemagLine) {
        freemagInProgress = true;
        showGiftLoader('Updating your offer...');
        var sections = getSectionsArray();
        var themeSectionId = getThemeSectionId();
        var sectionId = getCartDrawerSectionId();
        var root = getShopifyRoot();
        fetchWithRetry(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: freemagLine.key, quantity: 0 })
        }).then(function() {
          return fetchWithRetry(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: '', sections: sections.join(','), sections_url: '/cart' })
          });
        }).then(function(r) { return r.text(); }).then(function(text) {
          var updateData;
          try { updateData = JSON.parse(text); } catch (_) {}
          if (updateData && updateData.sections && Object.keys(updateData.sections).length > 0) {
            dispatchCartChange(updateData, themeSectionId, sectionId, Object.keys(updateData.sections));
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          freemagInProgress = false;
          hideGiftLoader();
        }).catch(function() {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          freemagInProgress = false;
          hideGiftLoader();
        });
      }
      return;
    }

    if (freemagLine) return;

    freemagInProgress = true;
    showGiftLoader('Adding your gift...');
    var sections = getSectionsArray();
    var themeSectionId = getThemeSectionId();
    var sectionId = getCartDrawerSectionId();
    var root = getShopifyRoot();
    fetchWithRetry(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { '_freemag_gift': '1' } }]
      })
    }).then(function(r) { return r.text().then(function(t) { return { text: t, ok: r.ok }; }); }).then(function(res) {
      var addData;
      try { addData = JSON.parse(res.text); } catch (_) {}
      if (addData && (addData.id || addData.items)) {
        fetchWithRetry(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: FREEMAG_DISCOUNT, sections: sections.join(','), sections_url: '/cart' })
        }).then(function(r2) { return r2.text().then(function(t2) { return { text: t2, ok: r2.ok }; }); }).then(function(updateRes) {
          var updateData;
          try { updateData = JSON.parse(updateRes.text); } catch (_) {}
          if (updateRes.ok && updateData && updateData.sections && Object.keys(updateData.sections).length > 0) {
            var sectionKeys = Object.keys(updateData.sections);
            var cartForDispatch = { sections: updateData.sections, item_count: updateData.item_count || cart.item_count + 1 };
            dispatchCartChange(cartForDispatch, themeSectionId, sectionId, sectionKeys);
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
          freemagInProgress = false;
          hideGiftLoader();
        }).catch(function() {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          freemagInProgress = false;
          hideGiftLoader();
        });
      } else {
        freemagInProgress = false;
        hideGiftLoader();
      }
    }).catch(function() {
      freemagInProgress = false;
      hideGiftLoader();
    });
  }

  document.addEventListener('cart:change', function(e) {
    var cart = e.detail && e.detail.cart;
    cartDebugLog('EVENT cart:change', { item_count: cart && cart.item_count, has_items: !!(cart && cart.items), items_len: cart && cart.items ? cart.items.length : 0, has_sections: !!(cart && cart.sections && Object.keys(cart.sections || {}).length) });
    if (!cart || !cart.items) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }
    var themeSectionId = getThemeSectionId();
    var hasSections = cart.sections && cart.sections[themeSectionId];
    setTimeout(function() {
      runFebOfferCheck(cart, 'cart:change');
      runSubscriptionGiftCheck(cart, 'cart:change');
      runFreemagCheck(cart, 'cart:change');
      if (!hasSections) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else if (!febofferInProgress && !subscriptionGiftInProgress && !febofferPending && !freemagInProgress) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }, 0);
  });
  var cartRefreshTimeout;
  var CART_REFRESH_DEBOUNCE_MS = 50;
  document.addEventListener('cart:refresh', function() {
    cartDebugLog('EVENT cart:refresh', { debounced: '50ms then fetch cart.js' });
    clearTimeout(cartRefreshTimeout);
    cartRefreshTimeout = setTimeout(function() {
      var root = getShopifyRoot();
      fetch(root + 'cart.js').then(function(r) { return r.text(); }).then(function(text) {
        var cart;
        try { cart = JSON.parse(text); } catch (_) {}
        cartDebugLog('EVENT cart:refresh got cart', { item_count: cart && cart.item_count, items_len: cart && cart.items ? cart.items.length : 0 });
        runFebOfferCheck(cart, 'cart:refresh');
        runSubscriptionGiftCheck(cart, 'cart:refresh');
        runFreemagCheck(cart, 'cart:refresh');
        setTimeout(function() {
          var canClear = !febofferInProgress && !subscriptionGiftInProgress && !febofferPending && !freemagInProgress;
          if (canClear) {
            setCheckoutUpdating(false);
          }
        }, 2000);
      }).catch(function() { setCheckoutUpdating(false); });
    }, CART_REFRESH_DEBOUNCE_MS);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      var root = getShopifyRoot();
      fetch(root + 'cart.js').then(function(r) { return r.text(); }).then(function(text) {
        var cart;
        try { cart = JSON.parse(text); } catch (_) {}
        runFebOfferCheck(cart, 'DOMContentLoaded');
        runSubscriptionGiftCheck(cart, 'DOMContentLoaded');
        runFreemagCheck(cart, 'DOMContentLoaded');
      }).catch(function() {});
    });
  } else {
    var root = getShopifyRoot();
    fetch(root + 'cart.js').then(function(r) { return r.text(); }).then(function(text) {
      var cart;
      try { cart = JSON.parse(text); } catch (_) {}
      runFebOfferCheck(cart, 'init');
      runSubscriptionGiftCheck(cart, 'init');
      runFreemagCheck(cart, 'init');
    }).catch(function() {});
  }
})();
